import React, {useEffect, useState} from "react";
import {Box, Fade, IconButton, InputBase, MenuItem, Modal, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {
  controlSx, FieldHelper, FieldLabel, modalCancelSx, ModalButton, modalPaperSx, modalSaveSx, SecretField, selectSx,
} from "../../../components/modals/formFieldShared";

const PROVIDERS = ["OpenAI", "Anthropic", "Google", "Meta", "Mistral", "Custom"];

const EMPTY_MODEL = {
  name: "",
  provider: "OpenAI",
  modelId: "",
  description: "",
  apiKey: "",
  baseUrl: "",
  organizationId: null,
};

const toFormModel = (model) => ({
  name: model.name || "",
  provider: model.provider || "OpenAI",
  modelId: model.modelId || "",
  description: model.description || "",
  apiKey: "",
  baseUrl: model.baseUrl || "",
  organizationId: model.organizationId ?? null,
});

export const AiModelDialog = ({open, handleClose, selectedModel, organizations, onSubmit}) => {
  const [model, setModel] = useState(EMPTY_MODEL);
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(selectedModel?.id);
  const isCustom = model.provider === "Custom";

  useEffect(() => {
    setModel(selectedModel ? toFormModel(selectedModel) : EMPTY_MODEL);
  }, [selectedModel, open]);

  const onChange = (e) => {
    const {name, value} = e.target;
    setModel(prev => ({...prev, [name]: value}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!model.name.trim() || !model.modelId.trim()) return;
    setLoading(true);
    const success = await onSubmit(model);
    setLoading(false);
    if (success) handleClose();
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Fade in={open}>
        <Box component={"form"} onSubmit={handleSubmit} sx={modalPaperSx(480)}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{p: 3, pb: 2}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              {isEdit ? "Edit" : "Add"} AI Model
            </Typography>
            <IconButton size={"small"} onClick={loading ? undefined : handleClose} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{px: 3, pb: 1, overflow: "auto"}}>
            <Box>
              <FieldLabel required>Display Name</FieldLabel>
              <Box sx={controlSx}>
                <InputBase
                  name={"name"} autoFocus placeholder={"e.g. GPT-4o"} value={model.name}
                  onChange={onChange} inputProps={{"aria-label": "Display name"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
            </Box>

            <Box>
              <FieldLabel required>Provider</FieldLabel>
              <Select
                value={model.provider}
                onChange={(e) => setModel(prev => ({...prev, provider: e.target.value}))}
                sx={selectSx}
                MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
              >
                {PROVIDERS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </Box>

            <Box>
              <FieldLabel required>Model ID</FieldLabel>
              <Box sx={controlSx}>
                <InputBase
                  name={"modelId"} placeholder={"e.g. gpt-4o"} value={model.modelId} disabled={isEdit}
                  onChange={onChange} inputProps={{"aria-label": "Model ID"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
              <FieldHelper>Exact identifier used in API calls</FieldHelper>
            </Box>

            {isCustom && (
              <Box>
                <FieldLabel>Base URL</FieldLabel>
                <Box sx={controlSx}>
                  <InputBase
                    name={"baseUrl"} placeholder={"https://your-endpoint.com/v1"} value={model.baseUrl}
                    onChange={onChange} inputProps={{"aria-label": "Base URL"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                  />
                </Box>
              </Box>
            )}

            {!isEdit && (
              <Box>
                <FieldLabel>API Key</FieldLabel>
                <SecretField name={"apiKey"} value={model.apiKey} onChange={onChange} placeholder={"sk-..."}/>
                <FieldHelper>Stored encrypted and cannot be changed after creation</FieldHelper>
              </Box>
            )}

            <Box>
              <FieldLabel>Description</FieldLabel>
              <Box sx={{...controlSx, height: "auto", alignItems: "flex-start", py: 1}}>
                <InputBase
                  name={"description"} multiline minRows={2} placeholder={"Short description of this model's capabilities"}
                  value={model.description} onChange={onChange} inputProps={{"aria-label": "Description"}}
                  sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
            </Box>

            <Box>
              <FieldLabel>Organization Access</FieldLabel>
              <Select
                value={model.organizationId ?? ""}
                onChange={(e) => setModel(prev => ({...prev, organizationId: e.target.value || null}))}
                sx={selectSx}
                MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
              >
                <MenuItem value={""}>Public (all users)</MenuItem>
                {organizations?.map(org => (
                  <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
              <FieldHelper>Leave empty to make this model available to all users</FieldHelper>
            </Box>
          </Stack>

          <Stack direction={"row"} gap={1.5} justifyContent={"flex-end"} sx={{p: 3, pt: 2}}>
            <ModalButton onClick={loading ? undefined : handleClose} sx={modalCancelSx}>Cancel</ModalButton>
            <ModalButton type={"submit"} disabled={loading} sx={modalSaveSx}>
              {isEdit ? "Update" : "Add Model"}
            </ModalButton>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};
